import { resetHoldAndWin } from "../../../State/Slices/holdAndWinSlice";
import React, { useEffect, useRef, useMemo } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import {
  setErrorRecoveryTransitionActive,
  setErrorRecoveryScreenCovered,
  resetPotCollection,
  clearPotCollectionWin,
  clearFeatureStateToBase,
  setFeatureSpinActive,
  setFeatureSpinComplete,
  setCriticalAnimationPlaying,
  setResetMainGameVisualEffects,
  refreshGameData,
  setSpinComplete,
  resetFreeSpins,
} from "../../../State/actions";
import {
  selectErrorRecoveryTransitionActive,
  selectCurrentRound,
} from "../../../State/selectors";
import store from "../../../State/Store/store";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { useReelControl } from "../../../Contexts/ReelControlContext";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import { useInitialIdleAnimations } from "../../../Hooks/useLandingAnimations";
import { useFreeSpinBasicInitialIdleAnimations } from "../../../Hooks/useFreeSpinBasicLandingAnimations";
import StorageManager from "../../../utils/StorageManager";
import { Settings } from "../../../settings";
import TickerTimeout from "../../../utils/TickerTimeout";
import anime from "animejs";
import * as PIXI from "pixi.js";

const isPrimaryTheme = Settings.APP.UI_THEME === "primary";

const ErrorRecoveryOverlay = () => {
  const dispatch = useDispatch();
  const { dimensions } = useDimensions();
  const { restoreReels } = useReelControl();
  // const { potControllersRef, gameViewControllerRef } = useReelRefs();
  const {
    potControllersRef,
    gameViewControllerRef,
    freeSpinBasicReelRefs,
    holdAndWinBoardRef,
    holdAndWinRespinCounterRef,
  } = useReelRefs();
  const { triggerInitialIdleAnimations } = useInitialIdleAnimations();
  const { triggerFreeSpinBasicIdleAnimations } =
    useFreeSpinBasicInitialIdleAnimations();
  const overlayRef = useRef(null);
  const overlayGraphicsRef = useRef(null);
  const animationRef = useRef(null);
  const animationRunningRef = useRef(false);
  const errorRecoveryTransitionActive = useSelector(
    selectErrorRecoveryTransitionActive,
  );
  const currentRound = useSelector(selectCurrentRound);
  // Keep a ref so animation callbacks always read the latest currentRound,
  // even if refreshGameData updates it after the animation has already started.
  const currentRoundRef = useRef(currentRound);
  currentRoundRef.current = currentRound;

  // Memoize container zIndex
  const containerZIndex = useMemo(() => 10000, []); // Above absolutely everything

  /**
   * Create overlay texture — theme-aware:
   * Primary: diagonal gradient matching the primary loader (#2a1f5e → #150e3a → #050020)
   * Secondary: solid black matching the secondary loader
   */
  const createOverlayTexture = (width, height) => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    if (isPrimaryTheme) {
      // Diagonal linear gradient — matches primary loader
      const linear = ctx.createLinearGradient(0, 0, width, height);
      linear.addColorStop(0, "#2a1f5e");
      linear.addColorStop(0.4, "#150e3a");
      linear.addColorStop(1, "#050020");
      ctx.fillStyle = linear;
    } else {
      // Solid black — matches secondary loader
      ctx.fillStyle = "#000000";
    }
    ctx.fillRect(0, 0, width, height);

    return PIXI.Texture.from(canvas);
  };

  // Initialize overlay sprite once on mount
  useEffect(() => {
    if (overlayRef.current && !overlayGraphicsRef.current) {
      const texture = createOverlayTexture(dimensions.width, dimensions.height);
      const sprite = new PIXI.Sprite(texture);
      sprite.width = dimensions.width;
      sprite.height = dimensions.height;

      overlayRef.current.addChild(sprite);
      overlayGraphicsRef.current = sprite;

      // Ensure container starts invisible
      overlayRef.current.alpha = 0;
    }

    return () => {
      if (overlayGraphicsRef.current) {
        if (overlayGraphicsRef.current.texture) {
          overlayGraphicsRef.current.texture.destroy(true);
        }
        overlayGraphicsRef.current.destroy();
        overlayGraphicsRef.current = null;
      }
    };
  }, [dimensions.width, dimensions.height]);

  // Update overlay dimensions when dimensions change
  useEffect(() => {
    if (overlayGraphicsRef.current) {
      // Destroy old texture
      if (overlayGraphicsRef.current.texture) {
        overlayGraphicsRef.current.texture.destroy(true);
      }
      // Create new gradient texture at new dimensions
      const texture = createOverlayTexture(dimensions.width, dimensions.height);
      overlayGraphicsRef.current.texture = texture;
      overlayGraphicsRef.current.width = dimensions.width;
      overlayGraphicsRef.current.height = dimensions.height;
    }
  }, [dimensions.width, dimensions.height]);

  // Handle error recovery transition animation
  useEffect(() => {
    if (
      errorRecoveryTransitionActive &&
      overlayRef.current &&
      !animationRunningRef.current
    ) {
      animationRunningRef.current = true;

      // Clear any existing animation
      if (animationRef.current) {
        animationRef.current.pause();
      }

      // Create transition: fade in black → hold → restore & setup → fade out
      // Phase 1: Fade in overlay (200ms)
      anime({
        targets: overlayRef.current,
        alpha: [0, 1],
        duration: 200,
        easing: "easeInQuad",
        complete: async () => {
          // Screen is now fully covered
          dispatch(setErrorRecoveryScreenCovered(true));

          // ── Full cleanup behind the curtain ──
          // Wipe local PC Redux so refresh can rehydrate from server truth.
          // Do NOT clear bonus localStorage yet — mid-bonus hydrate may need
          // flippedCoinPositions. Cleared only when server is NOT in bonus.
          const potCollectionState = store.getState().potCollection;
          const wasPotCollection =
            potCollectionState?.potCollectionActive ||
            potCollectionState?.potCollectionTriggered;

          if (wasPotCollection) {
            dispatch(resetPotCollection());
            dispatch(clearPotCollectionWin());
            dispatch(clearFeatureStateToBase());
            dispatch(setFeatureSpinActive(false));
            dispatch(setFeatureSpinComplete(true));
            dispatch(setCriticalAnimationPlaying(false));
            dispatch(setResetMainGameVisualEffects(true));

            // Visual reset: restore pot controller spines (skin + idle)
            // Mirrors the normal outro flow in GameView.jsx.
            // Pot Collection only — these are Zeus pot orbs and the pot text
            // labels baked into the Zeus game_view spine. GameViewSimple has
            // no such methods, so calling them unguarded throws.
            if (FEATURES_CONFIG.POT_COLLECTION.ENABLED) {
              const controllers = potControllersRef?.current;
              if (controllers) {
                const keyMap = { D: "double", M: "mystery", C: "collect" };
                for (const [, potType] of Object.entries(keyMap)) {
                  const ctrl = controllers[potType];
                  if (!ctrl) continue;
                  ctrl.stage = 0;
                  ctrl.reactivate();
                  ctrl.playIdle();
                }
              }

              // Restore pot text labels and hide bonus-only UI
              const gvc = gameViewControllerRef?.current;
              if (gvc) {
                gvc.showAllPotTexts?.();
                gvc.setRespinCounterVisible?.(false);
                gvc.setCoinCounterVisible?.(false);
              }
            }
          }

          // Clear stale FR localStorage
          const isFreeRoundsActive = store.getState().freeRounds?.status === "active";
          // if (isFreeRoundsActive) {
          // Never during a replay: the live round's record (2026-10-28).
          if (isFreeRoundsActive && !store.getState().replay?.sessionActive) {
            StorageManager.clearFreeRoundsRound();
          }

          // Re-sync with server — potCollectionSlice hydrates bonus if still active
          await dispatch(refreshGameData({
            useApiData: true,
            ignoreFreeRoundsStorage: true,
          }));

          const featureState = store.getState().game.featureState;
          const isBonusNow = featureState?.type === "bonus";

          // ── Free spins is a feature too ──
          //
          // This whole method asked `featureState` only, and detectFeatureState
          // knows the respin bonus and nothing else — so a recoverable error
          // inside a free-spins round fell into the base-return branch below:
          // clearFeatureStateToBase, setFeatureSpinActive(false),
          // setSpinComplete(true) and the base board repainted, while
          // freeSpinsSlice re-hydrated the SAME refresh as an active round. The
          // room stayed on screen with its spins routed back to the base game's
          // flags.
          //
          // The slice already hydrated itself from this refresh, so its own
          // `active` is the honest answer to "is the round still live?" — the
          // twin of liveBonusPhase, read the same way and used the same way.
          const liveFreeSpins = store.getState().freeSpins?.active === true;

          // ── An unanswered gamble is a live feature too ──
          //
          // A refused `gamble_freespins` / `collect_freespins` brings us here
          // with the gamble screen still open and the package still the
          // player's — the server settled nothing. `freeSpins.active` is false
          // (the package is won, not taken), so without this the base-return
          // branch below runs `resetFreeSpins`, wiping the very ladder this
          // refresh just rebuilt and leaving the screen with no data to offer.
          //
          // Same test the reload opener uses in GameViewSimple: the ladder is
          // open while it is unsettled AND still has outcomes left.
          const fsAfterRefresh = store.getState().freeSpins;
          const liveGamble =
            !fsAfterRefresh?.gambleSettled &&
            (fsAfterRefresh?.gamble?.variants?.length ?? 0) > 0;

          // Prefer store after refresh (ref may still hold pre-refresh round)
          const round = store.getState().game.currentRound || currentRoundRef.current;
          let reelsToRestore = null;

          if (
            round?.reels &&
            Array.isArray(round.reels) &&
            round.reels.length > 0
          ) {
            reelsToRestore = round.reels;
          }

          if (reelsToRestore) {
            const baseGame = StorageManager.loadBaseGameRound();
            const storedBet = baseGame?.bet ?? round?.bet;
            restoreReels(reelsToRestore, storedBet);
          }

          // ── Free spins and Hold & Win too (Vladimir, 2026-10-13: "must work
          // flawlessly on base, free, and hnw all same") ── a refusal freezes
          // THEIR boards mid-spin, and restoreReels knows only the base reels:
          // the room came back with its strips stuck half a cell off.
          const fsRound = store.getState().freeSpins?.currentRound;
          const fsReels = freeSpinBasicReelRefs?.current || [];
          // Not under a Hold & Win room — its board is the one that froze.
          const restoreFreeSpins =
            store.getState().freeSpins?.active === true &&
            !store.getState().holdAndWin?.active &&
            Array.isArray(fsRound?.reels) &&
            fsReels.length > 0;
          if (restoreFreeSpins) {
            const fsBet = fsRound.bet ?? store.getState().game?.bet ?? null;
            for (let i = 0; i < fsReels.length && i < fsRound.reels.length; i++) {
              if (Array.isArray(fsRound.reels[i])) {
                fsReels[i]?.restoreToIdleState?.(fsRound.reels[i], fsBet);
              }
            }
          }
          if (store.getState().holdAndWin?.active) {
            holdAndWinBoardRef?.current?.restore?.();
            // The refused respin had already counted down (−1 at its start):
            // back to the server's number.
            holdAndWinRespinCounterRef?.current?.setValue?.(
              store.getState().holdAndWin.respinsLeft,
            );
          }

          // Live bonus on server: intro / mid. Otherwise force full base return
          // (other tab finished bonus — stale LS must not leave type:"bonus").
          const liveBonusPhase =
            isBonusNow &&
            (featureState.phase === "intro" ||
              featureState.phase === "in_progress");

          let replayIntroWinSequence = false;
          if (liveBonusPhase && featureState.phase === "intro") {
            replayIntroWinSequence = true;
            dispatch(setResetMainGameVisualEffects(true));
          } else if (liveBonusPhase && featureState.phase === "in_progress") {
            // Mid-bonus respins restore handled below via reels/UI state
          } else if (liveFreeSpins) {
            // Round still live on the server: the slice has restored it and the
            // room owns the screen. Nothing to tear down — the reload popup and
            // the resume press take it from here, exactly as they do on a
            // refresh.
          } else if (liveGamble) {
            // The gamble screen is still the right screen and still holds the
            // right package. The refresh has already fed it the rebuilt ladder,
            // which is what re-arms its buttons. Nothing to tear down.
          } else {
            // ── Full return to base (server finished bonus / no live feature) ──
            // StorageManager.clearBonusGameRound();
            // StorageManager.clearTriggerWin();
            // Same rule as the bonus round above: the server says the feature
            // is over, so its stored round must not outlive it.
            // StorageManager.clearFreeSpinsRound();
            // Never during a replay: these are the LIVE round's records, which
            // the replay must leave as it found them (2026-10-28).
            if (!store.getState().replay?.sessionActive) {
              StorageManager.clearBonusGameRound();
              StorageManager.clearTriggerWin();
              StorageManager.clearFreeSpinsRound();
            }
            dispatch(resetFreeSpins());
            // ── And Cleopatra's Hold & Win (2026-10-07) ── its own slice is not
            // in the feature detection above; reset unless the server still
            // offers a respin (the refresh has then restored the bonus).
            if (!(store.getState().game?.availableActions || []).includes("respin")) {
              dispatch(resetHoldAndWin({ keepTotal: false }));
            }

            // Ensure getSpinParams sends "spin" not "respin"
            dispatch(resetPotCollection());
            dispatch(clearPotCollectionWin());
            dispatch(clearFeatureStateToBase());
            dispatch(setFeatureSpinActive(false));
            dispatch(setFeatureSpinComplete(true));
            dispatch(setCriticalAnimationPlaying(false));
            // Do NOT setResetMainGameVisualEffects here — it races with
            // triggerInitialIdleAnimations (reset kills idles; delayed texture
            // hide still runs → missing symbols). PC wipe above already ran
            // before await refresh when wasPotCollection.
            dispatch(setSpinComplete(true));

            // Visual cleanup — same as GameView outro midpoint.
            // This branch is the plain "no live feature" path, so it runs on
            // EVERY error recovery in the base game — which is why the
            // unguarded pot calls threw here and not in the branch above.
            if (FEATURES_CONFIG.POT_COLLECTION.ENABLED) {
              const controllers = potControllersRef?.current;
              if (controllers) {
                for (const potType of ["double", "mystery", "collect"]) {
                  const ctrl = controllers[potType];
                  if (!ctrl) continue;
                  ctrl.stage = 0;
                  ctrl.reactivate();
                  ctrl.playIdle();
                }
              }
              const gvc = gameViewControllerRef?.current;
              if (gvc) {
                gvc.showAllPotTexts?.();
                gvc.setRespinCounterVisible?.(false);
                gvc.setCoinCounterVisible?.(false);
                gvc.setFeatureSkin?.(null);
                gvc.destroyCoinCounter?.();
                gvc._stopRain?.();
                gvc.setGameState?.("main");
              }
            }
          }

          // Restart idle after restore (base + mid-bonus). Skip intro — win
          // controller replays trigger sequence after the curtain.
          if (reelsToRestore && !replayIntroWinSequence) {
            triggerInitialIdleAnimations();
          }
          if (restoreFreeSpins) triggerFreeSpinBasicIdleAnimations();

          // Phase 2: Fade out to reveal restored reels (200ms)
          animationRef.current = anime({
            targets: overlayRef.current,
            alpha: [1, 0],
            duration: 200,
            easing: "easeOutQuad",
            complete: () => {
              dispatch(setErrorRecoveryScreenCovered(false));
              animationRunningRef.current = false;
              dispatch(setErrorRecoveryTransitionActive(false));

              // After curtain: nudge win controller to replay trigger coins → PC intro
              if (replayIntroWinSequence) {
                dispatch(setResetMainGameVisualEffects(true));
                dispatch(setSpinComplete(false));
                new TickerTimeout(() => {
                  dispatch(setSpinComplete(true));
                }, 50);
              }
            },
          });
        },
      });
    }
  // }, [errorRecoveryTransitionActive, dispatch, restoreReels, triggerInitialIdleAnimations, potControllersRef, gameViewControllerRef]);
  }, [
    errorRecoveryTransitionActive,
    dispatch,
    restoreReels,
    triggerInitialIdleAnimations,
    triggerFreeSpinBasicIdleAnimations,
    potControllersRef,
    gameViewControllerRef,
    freeSpinBasicReelRefs,
    holdAndWinBoardRef,
    holdAndWinRespinCounterRef,
  ]);

  // Cleanup only on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        animationRef.current.pause();
        animationRef.current = null;
      }
    };
  }, []);

  // Always render, visibility controlled by alpha
  return (
    <Container
      name="error_recovery_overlay"
      ref={overlayRef}
      x={0}
      y={0}
      zIndex={containerZIndex}
    />
  );
};

export default ErrorRecoveryOverlay;
